// Helper function to convert title to kebab-case
const toKebabCase = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// Helper function to generate project paths
const generateProjectPaths = (title: string) => {
  const id = toKebabCase(title);
  return {
    id,
    markdownFile: id,
    image: `/projects/${id}/cover.jpg`,
    coverImage: `/projects/${id}/cover.jpg`,
    // Don't set heroImage by default - let it be undefined
  };
};

export interface Project {
  id: string;
  title: string;
  description: string;
  image: string;
  markdownFile: string;
  coverImage: string;
  heroImage?: string;
  awardImage?: string;
  tags?: string[];
  meta?: string;
  mitTag?: string;
  workInProgress?: boolean;
}

// Raw project data with just title, description, and tags
const rawProjects = [
    {
    title: 'Sensory Feedback',
    description: 'Helping a child with Spina Bifida learn to walk',
    tags: ['Accessibility','Wearables', 'Electronics'],
    meta: 'Group project, where I currently lead electronics design for the initial prototype',
    mitTag: 'This project directly embodies my goal to help people overcome physical barriers. It involves close collaboration with need-knowers to design a sensory feedback system, applying my technical skills to a deeply human-centered, assistive problem.',
    workInProgress: true,
  },
    {
    title: 'Sketch to AR',
    description: 'Transforming hand-drawn sketches into augmented reality',
    tags: ['AR', 'Generative Design', 'Interaction Design','AI'],
    meta: 'A project I led at Snap, with UI contributions from a designer',
    mitTag: 'This project demonstrates my ability to bridge physical creativity (sketching) with digital augmentation, aligning with Tangible Media\'s goal of making digital systems intuitive and manipulable through physical actions.'
  },
      {
    title: 'Dyslexia Reader',
    description: 'Simplifying reading in the browser for those with dyslexia',
    tags: ['Accessibility'],
    meta: 'A solo project',
    mitTag: 'This assistive technology project showcases my ability to work closely with need knowers and apply my understanding of software and interaction to create inclusive technologies, a goal I want to further explore at the Media Lab in embodied form.',
    workInProgress: true
  },


  {
    title: 'Hand Tracking for Gen AR',
    description: 'Unlocking experiences with hand transforms and gestures',
    tags: ['AR', 'AI', 'Generative Design','Interaction Design'],
    meta: 'Solo project at Snap',
    mitTag: 'This project explores using human movement and gesture as powerful alternative inputs, moving beyond screens to ground interaction in the body, a key theme in my objective to empower diverse physical capabilities.'
  },
        {
    title: 'Adaptive Acoustics',
    description: 'Diffusing sound through beautiful generative design',
    awardImage: '/projects/adaptive-acoustics/award.png',
    tags: ['Generative Design', 'Robotics','Fabrication','AI','Product Design'],
    meta: 'Group project created with colleague Ed Turner - I led generative design of panels and electronics, Ed led 3D design of digital interface and we fabricated the panels together',
    mitTag: 'This work combines robotic fabrication with generative design to create physical interfaces that respond to their environment, reflecting my interest in how computation can be given physical form and how material systems can respond to human needs.'
  },
    {
    title: 'Virtual Try On',
    description: 'Augmenting trying on clothes and accessories',
    tags: ['AR','AI', 'Interaction Design'],
    meta: 'Solo projects at Snap using tracking technologies created by my team / org',
    mitTag: 'Working on high-visibility AR experiences taught me to design interactions integrated with the physical world and translate creative work into widely used production-ready systems, insights I now hope to apply to accessibility and assistive design.'
  },

      {
    title: 'Drawing Robots',
    description: 'Robotic drawing and generative art',
    awardImage: '/projects/drawing-robots/award-logo.png',
    tags: ['Robotics', 'Product Design','Generative Design','Fabrication'],
    meta: 'Group project - I led the redesign of the robot\'s chassis',
    mitTag: 'My experience with mass-producing experimental drawing robots demonstrates my understanding materials and manufacturing - skills essential to creating physical, tangible interfaces.'
  },

    {
    title: 'Generative AR',
    description: 'Combining Generative AI and Augmented Reality',
    tags: ['AR', 'AI', 'Generative Design'],
    meta: 'Group project at Snap - I led design of Augmented Reality components',
    mitTag: 'This work explores the intersection of AI and AR, relevant to my interest in how advanced technologies can be harnessed to create new forms of interaction and participation. This is connected to the Critical Matter lab\'s focus on AI and computer vision.'
  },
  {
    title: 'Affinity Machine',
    description: 'An interactive installation, based on hacked retro phones',
    tags: ['Interaction Design', 'Electronics'],
    meta: 'Group project - I led cage design, worked on electronics with Andrew Barnhill and led a text-to-speech interactive experience',
    mitTag: 'Building this interactive installation highlights my experience with physical computing and creating engaging, tangible interfaces that connect people, reinforcing my desire to move beyond screen-based interactions.'
  },
  {
    title: 'Intelligent Footwear',
    description: 'Embedded sensors for smart footwear',
    awardImage: '/projects/intelligent-footwear/GoodDesign.png',
    tags: ['Wearables', 'Electronics'],
    meta: 'Solo project testing Elliot Wilson\'s sensor array and building custom sensors',
    mitTag: 'My work testing embedded sensors in shoe soles at Monash Sensilab provided foundational experience in wearable technology and physical sensing, which is crucial for developing embodied assistive technologies.'
  },

  //   {
  //   title: 'Digital Sketches',
  //   description: 'A series of small digital projects',
  //   tags: ['AI'],
  //   meta: 'Solo projects',
  // },
  {
    title: '3D Sketches',
    description: 'Some product design experiments, for fun',
    tags: ['Product Design'],
    meta: 'Solo Projects'
  },
  {
    title: 'Generative Chairs',
    description: 'Creating organic parametric forms through a physical interface',
    tags: ['Generative Design','Electronics','Interaction Design'],
    meta: 'Solo Projects',
    mitTag: 'This project explores creating organic forms through a physical interface, directly aligning with my interest in tangible media and critical matter - with how physical manipulation can make digital systems more intuitive.'
  },
  {
    title: 'Mold Tool Design',
    description: 'Designing a mold for a dental floss holder',
    tags: ['Product Design', 'Fabrication'],
    meta: 'Solo Projects',
    mitTag: 'This project demonstrates my skills in fabrication and tool design, providing the practical manufacturing knowledge needed to build robust physical assistive devices.'
  },
];

// Generate full project objects with auto-generated paths
export const projects: Project[] = rawProjects.map(project => ({
  ...project,
  ...generateProjectPaths(project.title),
}));

// Helper function to get project by id
export const getProjectById = (id: string): Project | undefined => {
  return projects.find(project => project.id === id);
};

// Helper function to get project by markdown file name
export const getProjectByMarkdownFile = (markdownFile: string): Project | undefined => {
  return projects.find(project => project.markdownFile === markdownFile);
};

// Helper function to get the hero image for a project (with fallback to cover)
export const getProjectHeroImage = (project: Project): string => {
  // If no hero image path is defined, use cover image
  if (!project.heroImage) {
    return project.coverImage;
  }
  
  // Return the hero image path if it exists
  return project.heroImage;
};

// Helper function to get the hero image path for a project
export const getProjectHeroImagePath = (project: Project): string => {
  const id = project.id;
  return `/projects/${id}/hero.jpg`;
};

// Helper function to check if hero image exists for a project
export const hasHeroImage = async (project: Project): Promise<boolean> => {
  if (!project.heroImage) return false;
  
  try {
    const response = await fetch(project.heroImage, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

// Helper function to get the best available hero image (with file existence check)
export const getProjectHeroImageWithFallback = async (project: Project): Promise<string> => {
  // If no hero image path is defined, use cover image
  if (!project.heroImage) {
    return project.coverImage;
  }
  
  // Check if hero image file actually exists
  try {
    const response = await fetch(project.heroImage, { method: 'HEAD' });
    if (response.ok) {
      return project.heroImage;
    }
  } catch {
    // If fetch fails, fall back to cover image
  }
  
  // Fall back to cover image if hero image doesn't exist
  return project.coverImage;
};
